import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Event, MessageActionType, UserAccessType, UserGroup, Message, AppComponentName, MessageType, MessageNoteCategory } from '@models';
import { ActionQueue, ActionQueueRules } from '@models/ActionQueue/ActionQueue.model';
import { ActionQueueService } from '@services/actionQueue.service';
import { AppMessageService } from '@services/message.service';
import { UserService } from '@services/user.service';
import { ChangeLogNotesComponent, ChangeLogNotesParams } from 'app/components/messages/changelog-notes/changelognotes.components';
import { Authorization } from 'app/shared/classes/authorization';
import { CommonShared } from 'app/shared/classes/common';
import { json } from 'd3';

import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';


export interface EditActionQueuesParams {
  events: Event[];
  userGroups: UserGroup[];
  associatedUserGroups: UserGroup[];
  actionQueue: ActionQueue;
  actionQueues: ActionQueue[];
}

@Component({
  standalone: false,
  selector: 'edit-action-queues',
  templateUrl: './edit-action-queues.component.html',
  styleUrls: ['./edit-action-queues.component.scss']
})

export class EditActionQueueComponent implements OnInit {
  
  auth = new Authorization();
  common = new CommonShared()

  events: Event[];
  eventsDisplayed: Event[] = [];
  eventSearchText: string = '';
  eventsSelected: Event[] = [];

  actionQueues: ActionQueue[] = [];
  actionQueue: ActionQueue = new ActionQueue();

  modalDragOn: boolean = false;
  actionQueuesModalRef: BsModalRef;
  username = 'unknown';
  changeLogNotes = '';
 
  associatedUserGroups: UserGroup[] = new Array<UserGroup>();
  userGroups: UserGroup[] = [];
  selectedUserGroup: UserGroup;

  batchReleaseConditionOptions = [
    { name: 'exactly', value: 'exactly' },
    { name: 'or more', value: 'or more' },
    { name: 'or less', value: 'or less' }
  ];

  userGroupConfig: any = {
    displayKey: 'name',
    search: true,
    height: '300px',
    placeholder: 'Select User Group',
    noResultsFound: 'No User Groups found!',
    searchOnKey: 'name'
  };
  
  EventsCols = [
    {field: 'checked', header: '', width: '5%'},
    {field: 'name', header: 'Sensors and Groups', width: '30%'},
    {field: 'description', header: 'Description', width: '65%'},
  ];

  constructor(public bsModalRef: BsModalRef, private toastr: ToastrService, private modalService: BsModalService,
               public _userService: UserService, private _actionQueueService: ActionQueueService,
               public _appMessageService: AppMessageService, public confirmationService: ConfirmationService,) {
  }


  ngOnInit() {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
        if (this.username != null && this.username !== '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
    this.loadInfoForActionQueue();
  }


  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  ngOnChanges() {
  }


  loadInfoForActionQueue() {
    this.events.forEach(x => { x.checked = false; });

    this.actionQueue.eventIds.forEach(ev => {
      var event = this.events.find(x => x.id == ev);
      if (event != null) {
        event.checked = true;
      }
    });
    
    this.eventsSelected = this.events.filter(x => x.checked);
    this.eventsDisplayed = this.events.filter(x => x.checked);

    if (this.actionQueue.userGroupId == null || this.actionQueue.userGroupId == 0) {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id === -1);
    } else {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id === this.actionQueue.userGroupId);
    }

    try {
      this.actionQueue.rulesObj = JSON.parse(this.actionQueue.rules);
      this.actionQueue.rulesObj.maxWaitInQueueDayStartHour = this.convertUtcHourToLocalHour(this.actionQueue.rulesObj.maxWaitInQueueDayStartHour);
      this.actionQueue.rulesObj.maxWaitInQueueNightStartHour = this.convertUtcHourToLocalHour(this.actionQueue.rulesObj.maxWaitInQueueNightStartHour); 
    }
    catch (e) {
      this.actionQueue.rulesObj = new ActionQueueRules();
    }
  }


  checkForDuplicateName(): boolean {
    const duplicate = this.actionQueues.find(x => x.name === this.actionQueue.name);
    if (duplicate && duplicate.id !== this.actionQueue.id) {
      this.toastr.warning('An Action Queue with this name already exists.');
      return true;
    }
    return false;
  }


  createOrUpdateActionQueue() {
    if (this.checkForDuplicateName()) {
      return;
    }

    if (this.actionQueue.id == null || this.actionQueue.id == 0) {
      this.createActionQueue();
    } else {
      this.updateActionQueue();
    }
  }


  createActionQueue() {
    this.updateActionQueueInfo(this.actionQueue);

    this._actionQueueService.createActionQueue(this.actionQueue).subscribe(
      results => {
        if (results != null) {
          this.toastr.success('Successfully created ' + this.actionQueue.name);
          this.bsModalRef.hide();
        } else {
          this.toastr.warning('Not found.');
        }
        return;
      },
      () => {
        this.toastr.error('Error occurred while creating Action Queue.');
      });
  }


  updateActionQueue() {
    this.updateActionQueueInfo(this.actionQueue);

    this._actionQueueService.updateActionQueues(this.actionQueue, '').subscribe(
      results => {
        if (results != null) {
          this.toastr.success('Successfully updated ' + this.actionQueue.name);
          this.bsModalRef.hide();
        } else {
          this.toastr.warning('Not found.');
        }
        return;
      },
      () => {
        this.toastr.error('Error occurred while updating Action Queue.');
      });
  }


  updateActionQueueInfo(actionQueue: ActionQueue) {
    actionQueue.eventIds = this.eventsSelected.map(x => x.id);

    actionQueue.userName = this.username;

    if (this.selectedUserGroup != null && this.selectedUserGroup.id > -1) {
      actionQueue.userGroupId = this.selectedUserGroup.id;
      actionQueue.userGroupName = this.selectedUserGroup.name;
      actionQueue.userId = 0;
    }
    else {
      actionQueue.userGroupId = 0;
    }

    // Update day and night start hours for bounds and convert to UTC

    var rulesCopy = Object.assign({}, actionQueue.rulesObj);

    if (rulesCopy.maxWaitInQueueDayStartHour < 0 || rulesCopy.maxWaitInQueueDayStartHour > 23) {
      rulesCopy.maxWaitInQueueDayStartHour = 9;
    }
    if (rulesCopy.maxWaitInQueueNightStartHour < 0 || rulesCopy.maxWaitInQueueNightStartHour > 23) {
      rulesCopy.maxWaitInQueueNightStartHour = 22;
    }

    rulesCopy.maxWaitInQueueDayStartHour = this.convertLocalHourToUtcHour(rulesCopy.maxWaitInQueueDayStartHour);
    rulesCopy.maxWaitInQueueNightStartHour = this.convertLocalHourToUtcHour(rulesCopy.maxWaitInQueueNightStartHour); 

    actionQueue.rules = JSON.stringify(rulesCopy);
  }


  convertLocalHourToUtcHour(localHour: number): number {
    var localTime = moment().hour(localHour).minute(0).second(0).millisecond(0).format('YYYY-MM-DD HH:mm:ss');
    var utcTime = moment(localTime).utc();
    return utcTime.hour();
  }


  convertUtcHourToLocalHour(utcHour: number): number {
    var utcTime = moment().hour(utcHour).minute(0).second(0).millisecond(0).format('YYYY-MM-DD HH:mm:ss');
    var localTime = moment.utc(utcTime).local();
    return localTime.hour();
  }


  eventsSelectionChanged(event: Event) {
    this.eventsSelected = this.events.filter(x => x.checked);
  } 


  searchEventsTextChanged() {
    var searchResults: Event[] = [];
    const searchLimit = 1000;
    let checkedCount = 0;
    
    if (this.eventSearchText == undefined || this.eventSearchText == '' || this.eventSearchText == 'null') {
      searchResults = [];
    }
    else if (this.eventSearchText == '*') {
      searchResults = this.events;
    }
    else {
      this.eventSearchText = this.eventSearchText.toLowerCase();
      searchResults = this.events.filter(s => (s.name != undefined ? s.name.toLowerCase().includes(this.eventSearchText) : false) ||
                                                    (s.description != undefined ? s.description.toLowerCase().includes(this.eventSearchText) : false) ||
                                                  (s.checked));
    }

    const displayCount = (searchResults.length > checkedCount + searchLimit) ? checkedCount + searchLimit : searchResults.length;

    var eventIds = searchResults.slice(0, displayCount).map(x => x.id);

    var eventsInOtherActionQueues = this.actionQueues.filter(aq => aq.id != this.actionQueue.id).map(aq => aq.eventIds).flat();

    this.eventsDisplayed = this.events.filter(x => eventIds.includes(x.id) && !eventsInOtherActionQueues.includes(x.id) && x.userFeatureAccess['EditEvent']);

    this.eventsDisplayed.sort((a, b) => {
      if (b.name > a.name) {
        return -1;
      }
      if (b.name < a.name) {
        return 1;
      }
    }); 
  }


  toggleCheckedEvents() {
    var newState = !this.eventsDisplayed[0].checked;
    this.eventsDisplayed.forEach(event => {
      event.checked = newState;
    });
  }


  createMessageData(msgActionType: MessageActionType, actionQueue: ActionQueue, msgNote: string): void {
    const message: Message = {
      id: 0,
      messageType: MessageType.Audit,
      appComponentName: AppComponentName.EventAndActions,
      actionType: msgActionType,
      appComponentItem: actionQueue.name,
      details: msgNote,
      userNotes: this.changeLogNotes,
      count: 1,
      acknowledged: false,
      userName: this.username,
      userGroupId: actionQueue.userGroupId
    };

    this._appMessageService.createAppMessage(message).subscribe(
      results => {
        if (results != null) {
          //this.toastr.success('Success');
        } else {
          //this.toastr.warning('Not found.');
        }
        return;
      },
      () => {
        //this.toastr.error('Error occurred while saving change log.');
      });
  }

  
  addNotes2ChangeLog() {
    this.changeLogNotes = '';

    const params: ChangeLogNotesParams = {
      changeLogNotes: this.changeLogNotes,
      userGroupId: this.selectedUserGroup.id,
      category: MessageNoteCategory.EventEdit
    };

    var modalRef = this.modalService.show(ChangeLogNotesComponent, { animated: false, backdrop: false, ignoreBackdropClick: true, class: 'modal-above-confirmdialog', initialState: params });
    modalRef.content.onChangeLogNotes.subscribe((value) => {
      this.changeLogNotes = value;
    });
  }

    
  showSelectedEvents() {
    this.eventsDisplayed = this.events.filter(x => x.checked);
  }


}